// Read-only report models. No persistence, stock changes or network access.
import { buildTransferRecommendation, movementLabel } from './mcTransferRecommendation.js';

export function reportNumber(value) {
  if (!['number', 'string'].includes(typeof value)) return null;
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const clean = String(value).trim().replace(/^R\s*/i, '').replace(/,/g, '').replace(/\s/g, '');
  const number = Number(clean);
  return Number.isFinite(number) ? number : null;
}

const COST_FIELDS = ['supply_price', 'supplyPrice', 'supplyCost', 'cost_price', 'costPrice', 'purchase_price', 'purchasePrice', 'unit_cost', 'unitCost', 'average_cost', 'avg_cost', 'attributed_cost', 'cost'];
const isSellingPrice = (field) => /retail|selling|sellprice|saleprice|^price$|^unitprice$|^rrp$|^msrp$/i.test(String(field || '').replace(/[\s_-]/g, ''));

// Keep zero distinct from missing. POS normalisation can copy retail into
// `cost`; when mapped to retail, that alias cannot prove a purchase cost.
export function resolveDeadStockCost(row = {}, mappedField = '', prepared = null, sourceIsPrepared = false) {
  const keys = Object.keys(row || {});
  const actualKey = (field) => keys.find((key) => key.toLowerCase() === String(field).toLowerCase());
  const fields = [...new Set([...(mappedField && !isSellingPrice(mappedField) ? [mappedField] : []), ...COST_FIELDS])];
  for (const field of fields) {
    if (field === 'cost' && isSellingPrice(mappedField)) continue;
    const key = actualKey(field);
    if (!key) continue;
    const amount = reportNumber(row[key]);
    if (amount === null || amount < 0) continue;
    // Metadata from Unit Intelligence converts a mapped price to the same
    // imported stock unit as DS1's system SOH, rather than multiplying a
    // per-base-unit cost by a number of packs.
    const meta = sourceIsPrepared ? row : prepared;
    let multiplier = 1;
    if (sourceIsPrepared && ['cost', 'effectiveCost'].includes(field)) multiplier = reportNumber(meta?.systemUnitSize) || 1;
    else if (key.toLowerCase() === String(mappedField).toLowerCase() && meta) {
      const systemSize = reportNumber(meta.systemUnitSize) || 1;
      const priceSize = reportNumber(meta.priceUnitSize) || 1;
      multiplier = meta.priceBasis === 'base' ? systemSize : systemSize / priceSize;
    }
    const cost = amount * multiplier;
    if (Number.isFinite(cost) && cost >= 0) return { cost, costKnown: true, costField: key };
  }
  return { cost: 0, costKnown: false, costField: '' };
}

export function reportCost(row = {}) {
  const cost = reportNumber(row.cost);
  if (row.costKnown === false || cost === null || cost < 0) return null;
  // Older callers did not retain missing-cost metadata; only a positive
  // value is usable for those rows. Explicit known zero remains valid.
  return row.costKnown === true || cost > 0 ? cost : null;
}

const safeDate = (value) => Number.isFinite(Date.parse(value || '')) ? new Date(value).toISOString() : '';
const scopeKey = (value) => String(value || '').trim().toLowerCase();

export function summarizeDeadStock(products = []) {
  const rows = (Array.isArray(products) ? products : []).filter((row) => reportNumber(row?.soh) > 0).map((row) => {
    const soh = reportNumber(row.soh);
    const cost = reportCost(row);
    const value = cost === null ? null : soh * cost;
    return { ...row, soh, cost, costKnown: cost !== null && Number.isFinite(value),
      tiedValue: value !== null && Number.isFinite(value) ? value : null };
  }).sort((a, b) => (b.tiedValue ?? -1) - (a.tiedValue ?? -1)
    || (reportNumber(b.daysStatic) || 0) - (reportNumber(a.daysStatic) || 0)
    || String(a.product || '').localeCompare(String(b.product || '')));
  const valuedRows = rows.filter((row) => row.costKnown);
  return { rows, count: rows.length, totalUnits: rows.reduce((sum, row) => sum + row.soh, 0),
    tiedValue: valuedRows.reduce((sum, row) => sum + row.tiedValue, 0),
    valuedCount: valuedRows.length, missingCostCount: rows.length - valuedRows.length,
    longestStatic: rows.reduce((age, row) => Math.max(age, reportNumber(row.daysStatic) || 0), 0) };
}

export function buildDeadStockReport({ deadStock = {}, scope = '', site = '', exportedAt = new Date().toISOString() } = {}) {
  if (!scopeKey(scope) || scopeKey(scope) === 'all') throw new Error('Select a site to export its dead stock.');
  if (!deadStock.hasWindow || deadStock.latestRevisionTrusted !== true) throw new Error('Wait for a checked stock update and 7 days of history before exporting.');
  if (!safeDate(exportedAt)) throw new Error('A valid export time is required.');
  return { kind: 'site', scope, site: site || scope, exportedAt: safeDate(exportedAt), latestAt: safeDate(deadStock.latestSnapshotAt || deadStock.latestAt),
    thresholdDays: reportNumber(deadStock.thresholdDays) || 7, ...summarizeDeadStock(deadStock.products) };
}

export function transferReportTitle(recommendation = {}) {
  if (recommendation.status === 'SETUP') return 'Choose your stock rules once';
  if (recommendation.status !== 'SUGGESTION') return 'No transfer suggested yet';
  const proposal = recommendation.proposal;
  const allocations = (proposal?.destinations || []).filter((row) => row.quantity > 0);
  if (allocations.length === 1) return `Send ${allocations[0].quantity} ${allocations[0].quantity === 1 ? 'unit' : 'units'} to ${allocations[0].site}`;
  if (allocations.length > 1) return `Split ${proposal.totalQuantity} units across ${allocations.length} sites`;
  if (proposal?.heldDestinations) return 'Check receiving sites';
  return proposal?.destinations.some((row) => row.netNeed > 0) ? 'Keep the stock at this site' : 'No transfer needed';
}

export function buildDeadStockProductReport({ item = {}, rows = [], sourceScope = '', site = '', rules = null, review = null,
  latestAt = '', exportedAt = new Date().toISOString() } = {}) {
  const at = safeDate(exportedAt);
  const sku = String(item.sku || '').trim();
  if (!at || !sku) throw new Error('A product SKU and a valid export time are required.');
  const allRows = Array.isArray(rows) ? rows.filter((row) => row && typeof row === 'object') : [];
  // Every loaded site is retained, regardless of the modal's movement filter.
  if (allRows.some((row) => row.sku && scopeKey(row.sku) !== scopeKey(sku))) throw new Error('The loaded movement belongs to a different product. Reopen this product.');
  const source = allRows.find((row) => scopeKey(row.scope) === scopeKey(sourceScope));
  const sourceSoh = reportNumber(source?.soh);
  const soh = sourceSoh === null ? reportNumber(item.soh) : sourceSoh;
  const cost = reportCost(source && Object.prototype.hasOwnProperty.call(source, 'costKnown') ? source : item);
  const tiedValue = cost === null || soh === null || soh < 0 ? null : cost * soh;
  // Recheck freshness at export time; reopening/exporting never refreshes stock.
  const recommendation = buildTransferRecommendation({ rows: allRows, sourceScope, rules, review, evaluatedAt: at });
  const proposal = recommendation.proposal;
  const allocations = (proposal?.destinations || []).filter((row) => row.quantity > 0);
  const sourceRemaining = proposal?.sourceRemaining ?? soh;
  return { kind: 'product', site: source?.site || site || sourceScope, sourceScope, exportedAt: at,
    latestAt: safeDate(source?.latestSnapshotAt || latestAt), product: item.product || source?.product || sku, sku,
    soh, cost, tiedValue: Number.isFinite(tiedValue) ? tiedValue : null,
    daysStatic: reportNumber(source?.daysStatic) ?? reportNumber(item.daysStatic),
    lastMovementAt: safeDate(source?.lastObservedMovement || item.lastMovementAt),
    observedSinceAt: safeDate(source?.observedSince || item.observedSinceAt),
    networkRows: allRows.map((row) => ({ ...row, movement: movementLabel(row.networkSignal) })),
    recommendation, title: transferReportTitle(recommendation), rules, allocations, sourceRemaining,
    transferValue: cost === null ? null : (proposal?.totalQuantity || 0) * cost,
    remainingValue: cost === null || sourceRemaining === null ? null : sourceRemaining * cost,
    checked: recommendation.checked === true };
}
